import React from "react";
import { Link } from "react-router-dom";
import styles from "./HeroSection.module.css";
import { Container } from "../../common/Container";
import { heroData } from "../../../data/sharedSpendData";

const SparkIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width={14} height={14}>
    <path
      fillRule="evenodd"
      d="M9 4.5a.75.75 0 01.721.544l.813 2.846a3.75 3.75 0 002.576 2.576l2.846.813a.75.75 0 010 1.442l-2.846.813a3.75 3.75 0 00-2.576 2.576l-.813 2.846a.75.75 0 01-1.442 0l-.813-2.846a3.75 3.75 0 00-2.576-2.576l-2.846-.813a.75.75 0 010-1.442l2.846-.813A3.75 3.75 0 007.466 7.89l.813-2.846A.75.75 0 019 4.5z"
      clipRule="evenodd"
    />
  </svg>
);

const CheckIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" width={16} height={16}>
    <path d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
  </svg>
);

export const HeroSection: React.FC = () => {
  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <section className={styles.hero}>
      <div className={styles.backgroundGlow} />
      <Container>
        <div className={styles.content}>
          <div className={styles.textContent}>
            <div className={styles.badge}>
              <span className={styles.badgeIcon}>
                <SparkIcon />
              </span>
              <span>{heroData.badge}</span>
            </div>

            <h1 className={styles.headline}>
              {heroData.headline.split("\n").map((line, i) => (
                <React.Fragment key={i}>
                  {line}
                  {i === 0 && <br />}
                </React.Fragment>
              ))}
            </h1>
            <p className={styles.subheadline}>{heroData.subheadline}</p>

            <div className={styles.features}>
              <div className={styles.featureItem}>
                <CheckIcon />
                <span>Real-time shared budgets</span>
              </div>
              <div className={styles.featureItem}>
                <CheckIcon />
                <span>Smart spend alerts</span>
              </div>
              <div className={styles.featureItem}>
                <CheckIcon />
                <span>No spreadsheets needed</span>
              </div>
            </div>

            <div className={styles.ctas}>
              <Link to={heroData.waitlistUrl} className={styles.primaryCta}>
                {heroData.ctaPrimary}
              </Link>
              <button
                onClick={() => scrollToSection("features")}
                className={styles.secondaryCta}
              >
                {heroData.ctaSecondary}
              </button>
            </div>
          </div>

          <div className={styles.mockupContainer}>
            <div className={styles.mockupGlow} />
            <div className={styles.budgetCard}>
              <div className={styles.cardHeader}>
                <div className={styles.cardTitle}>Household Budget</div>
                <div className={styles.cardBadge}>Monthly</div>
              </div>
              <div className={styles.cardAmounts}>
                <span className={styles.cardSpent}>$1,247</span>
                <span className={styles.cardTotal}>&nbsp;/ $2,000</span>
              </div>
              <div className={styles.cardProgressTrack}>
                <div className={styles.cardProgressBar} />
              </div>
              <div className={styles.cardMeta}>
                <span className={styles.cardRemaining}>$753 remaining</span>
                <span className={styles.cardDays}>12 days left</span>
              </div>
              <div className={styles.cardMembers}>
                <div className={styles.memberAvatar}>J</div>
                <div className={styles.memberAvatar}>S</div>
                <span className={styles.memberLabel}>2 members</span>
              </div>
              <div className={styles.cardDivider} />
              <div className={styles.cardExpenses}>
                <div className={styles.expenseItem}>
                  <div className={styles.expenseInfo}>
                    <span className={styles.expenseDot} />
                    <span className={styles.expenseName}>Groceries</span>
                  </div>
                  <span className={styles.expenseAmount}>-$82.50</span>
                </div>
                <div className={styles.expenseItem}>
                  <div className={styles.expenseInfo}>
                    <span className={styles.expenseDot} />
                    <span className={styles.expenseName}>Electric bill</span>
                  </div>
                  <span className={styles.expenseAmount}>-$94.00</span>
                </div>
                <div className={styles.expenseItem}>
                  <div className={styles.expenseInfo}>
                    <span className={styles.expenseDot} />
                    <span className={styles.expenseName}>Gas</span>
                  </div>
                  <span className={styles.expenseAmount}>-$45.20</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Container>
    </section>
  );
};
